"use client";

import { CreditCard } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { cardBrandLabel } from "../../utils/card-brand-label";

interface CardBrandIconProps {
  brand?: string;
  className?: string;
  /**
   * When true (default) the icon is decorative — rendered with aria-hidden.
   * Pass false when the icon is the sole accessible label for the brand;
   * the component will render role="img" + aria-label instead.
   */
  decorative?: boolean;
}

// SVGs use canonical brand colors that hit ≥ 4.5:1 contrast on both
// bg-background and bg-muted/30 in light + dark modes.

function VisaIcon({ className, ...aria }: React.SVGProps<SVGSVGElement> & { className?: string }) {
  return (
    <svg
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...aria}
    >
      <rect width="38" height="24" rx="4" fill="#FFFFFF" stroke="#E6E6E6" strokeWidth="0.5" />
      <text
        x="19"
        y="16"
        textAnchor="middle"
        fill="#1A1F71"
        fontSize="9"
        fontWeight="900"
        fontStyle="italic"
        fontFamily="Arial, sans-serif"
        letterSpacing="0.6"
      >
        VISA
      </text>
    </svg>
  );
}

function PayPalIcon({ className, ...aria }: React.SVGProps<SVGSVGElement> & { className?: string }) {
  return (
    <svg
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...aria}
    >
      <rect width="38" height="24" rx="4" fill="#FFFFFF" stroke="#E6E6E6" strokeWidth="0.5" />
      <text
        x="9.5"
        y="16"
        fill="#003087"
        fontSize="8"
        fontWeight="800"
        fontStyle="italic"
        fontFamily="Arial, sans-serif"
      >
        Pay
      </text>
      <text
        x="20.5"
        y="16"
        fill="#009CDE"
        fontSize="8"
        fontWeight="800"
        fontStyle="italic"
        fontFamily="Arial, sans-serif"
      >
        Pal
      </text>
    </svg>
  );
}

function MastercardIcon({ className, ...aria }: React.SVGProps<SVGSVGElement> & { className?: string }) {
  return (
    <svg
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...aria}
    >
      <rect width="38" height="24" rx="4" fill="#252525" />
      <circle cx="15" cy="12" r="7" fill="#EB001B" />
      <circle cx="23" cy="12" r="7" fill="#F79E1B" />
      <path
        d="M19 6.8a7 7 0 0 1 0 10.4A7 7 0 0 1 19 6.8Z"
        fill="#FF5F00"
      />
    </svg>
  );
}

function AmexIcon({ className, ...aria }: React.SVGProps<SVGSVGElement> & { className?: string }) {
  return (
    <svg
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...aria}
    >
      <rect width="38" height="24" rx="4" fill="#2E77BC" />
      <text
        x="19"
        y="16"
        textAnchor="middle"
        fill="white"
        fontSize="9"
        fontWeight="bold"
        fontFamily="Arial, sans-serif"
        letterSpacing="0.5"
      >
        AMEX
      </text>
    </svg>
  );
}

function DiscoverIcon({ className, ...aria }: React.SVGProps<SVGSVGElement> & { className?: string }) {
  return (
    <svg
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...aria}
    >
      <rect width="38" height="24" rx="4" fill="#FFFFFF" stroke="#E6E6E6" strokeWidth="0.5" />
      <circle cx="24" cy="12" r="7" fill="#F76F20" />
      <text
        x="9"
        y="16"
        fill="#231F20"
        fontSize="6.5"
        fontWeight="bold"
        fontFamily="Arial, sans-serif"
      >
        DISCOVER
      </text>
    </svg>
  );
}

function JcbIcon({ className, ...aria }: React.SVGProps<SVGSVGElement> & { className?: string }) {
  return (
    <svg
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...aria}
    >
      <rect width="38" height="24" rx="4" fill="#FFFFFF" stroke="#E6E6E6" strokeWidth="0.5" />
      <rect x="4" y="4" width="10" height="16" rx="5" fill="#003087" />
      <rect x="14" y="4" width="10" height="16" rx="5" fill="#CC0000" />
      <rect x="24" y="4" width="10" height="16" rx="5" fill="#007B40" />
      <text x="9" y="15" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold" fontFamily="Arial, sans-serif">J</text>
      <text x="19" y="15" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold" fontFamily="Arial, sans-serif">C</text>
      <text x="29" y="15" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold" fontFamily="Arial, sans-serif">B</text>
    </svg>
  );
}

function DinersIcon({ className, ...aria }: React.SVGProps<SVGSVGElement> & { className?: string }) {
  return (
    <svg
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...aria}
    >
      <rect width="38" height="24" rx="4" fill="#FFFFFF" stroke="#E6E6E6" strokeWidth="0.5" />
      <circle cx="19" cy="12" r="8" fill="none" stroke="#004A97" strokeWidth="1.5" />
      <circle cx="15.5" cy="12" r="5" fill="none" stroke="#004A97" strokeWidth="1" />
      <circle cx="22.5" cy="12" r="5" fill="none" stroke="#004A97" strokeWidth="1" />
    </svg>
  );
}

function UnionPayIcon({ className, ...aria }: React.SVGProps<SVGSVGElement> & { className?: string }) {
  return (
    <svg
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...aria}
    >
      <rect width="38" height="24" rx="4" fill="#FFFFFF" stroke="#E6E6E6" strokeWidth="0.5" />
      <rect x="3" y="3" width="12" height="18" rx="3" fill="#CC0000" />
      <rect x="13" y="3" width="12" height="18" rx="3" fill="#CC0000" />
      <rect x="23" y="3" width="12" height="18" rx="3" fill="#007B40" />
      <rect x="15" y="3" width="8" height="18" fill="#CC0000" />
      <text x="19" y="15" textAnchor="middle" fill="white" fontSize="5" fontWeight="bold" fontFamily="Arial, sans-serif">UP</text>
    </svg>
  );
}

function StripeLinkIcon({ className, ...aria }: React.SVGProps<SVGSVGElement> & { className?: string }) {
  return (
    <svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" className={className} {...aria}>
      <rect width="38" height="24" rx="4" fill="#00D66F" />
      <text
        x="19"
        y="16.5"
        textAnchor="middle"
        fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif"
        fontSize="9"
        fontWeight="700"
        fill="#011E0F"
      >
        Link
      </text>
    </svg>
  );
}

function CartesBancairesIcon({ className, ...aria }: React.SVGProps<SVGSVGElement> & { className?: string }) {
  return (
    <svg
      viewBox="0 0 38 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...aria}
    >
      <rect width="38" height="24" rx="4" fill="#00529B" />
      <text
        x="19"
        y="16"
        textAnchor="middle"
        fill="white"
        fontSize="7"
        fontWeight="bold"
        fontFamily="Arial, sans-serif"
        letterSpacing="0.3"
      >
        CB
      </text>
    </svg>
  );
}

export function PayPalBrandIcon({
  className,
  decorative = true,
}: Omit<CardBrandIconProps, "brand">) {
  const baseClass = cn("h-5 w-auto shrink-0", className);
  const ariaProps = decorative
    ? { "aria-hidden": true as const }
    : { role: "img" as const, "aria-label": "PayPal" };
  return <PayPalIcon className={baseClass} {...ariaProps} />;
}

export function CardBrandIcon({
  brand,
  className,
  decorative = true,
}: CardBrandIconProps) {
  const key = (brand ?? "").toLowerCase().trim();
  const baseClass = cn("h-5 w-auto shrink-0", className);

  const ariaProps = decorative
    ? { "aria-hidden": true as const }
    : { role: "img" as const, "aria-label": cardBrandLabel(brand) };

  if (/^visa\b/.test(key)) {
    return <VisaIcon className={baseClass} {...ariaProps} />;
  }
  if (/^master/.test(key)) {
    return <MastercardIcon className={baseClass} {...ariaProps} />;
  }
  if (/^american|^amex/.test(key)) {
    return <AmexIcon className={baseClass} {...ariaProps} />;
  }
  if (/^discover/.test(key)) {
    return <DiscoverIcon className={baseClass} {...ariaProps} />;
  }
  if (/^jcb/.test(key)) {
    return <JcbIcon className={baseClass} {...ariaProps} />;
  }
  if (/^diners/.test(key)) {
    return <DinersIcon className={baseClass} {...ariaProps} />;
  }
  if (/^union/.test(key)) {
    return <UnionPayIcon className={baseClass} {...ariaProps} />;
  }
  if (/^cartes/.test(key)) {
    return <CartesBancairesIcon className={baseClass} {...ariaProps} />;
  }
  if (/^link|^stripe.?link/.test(key)) {
    return <StripeLinkIcon className={baseClass} {...ariaProps} />;
  }

  return (
    <CreditCard
      className={cn("h-5 w-5 shrink-0 text-muted-foreground", className)}
      {...ariaProps}
    />
  );
}
